/* ===================== BLOG COMPOSER (ASTROLOGER) =====================
   Write or edit one article and either keep it as a draft or hand it to the
   admin's review queue — POST/PUT /my-blog-posts, with `submit: true` doing
   the save and the submission in one request.

   Two things about this form are deliberate and worth knowing before
   changing it:

   1. **The body is a textarea, not a rich-text editor.** The API takes plain
      text and renders the HTML itself (see blogMarkup.js), because the
      public article page injects the body with innerHTML — accepting an
      author's markup would be stored XSS with only an admin's eye in the
      way. The formatting cheatsheet comes from BLOG_MARKUP_HELP (@astro/api)
      so it always matches what the server actually supports, and "Preview"
      renders the same subset locally.

   2. **Nothing here chooses a status.** The buttons are "Save draft" and
      "Submit for review"; the server decides everything else. A published
      or in-review article is not editable at all (the API answers 409), so
      this modal only ever opens on a draft, a rejected post, or a new one.

   Same form idiom as the rest of the dashboard: react-hook-form (@astro/forms),
   a react-select for the category (never a native select — house rule), the
   shared Field/input skin, and Laravel 422 field errors mapped inline.
   ===================================================================== */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Select from 'react-select'
import { api, BLOG_MARKUP_HELP } from '@astro/api'
import { Controller, useForm } from '@astro/forms'
import { selectStyles } from '@astro/astrologer-forms'
import { LuEye, LuImage, LuPencil, LuSend, LuTrash2 } from 'react-icons/lu'
import { renderAuthorContent, readingStats } from './blogMarkup'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

const MAX_TITLE = 255
const MAX_EXCERPT = 500
const MAX_CONTENT = 60000
const MAX_IMAGE_BYTES = 10 * 1024 * 1024

function Field({ label, error, hint, className = '', children }) {
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600">{label}</label>
        {hint && <span className="text-[10px] text-stone-400">{hint}</span>}
      </div>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/** Cover image: upload straight to the API, keep the URL it hands back. */
function CoverImageField({ value, onChange, onError }) {
  const fileRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  const pick = async (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      onError('The cover must be an image file.')
      return
    }
    if (file.size > MAX_IMAGE_BYTES) {
      onError('The cover image must be under 10MB.')
      return
    }
    setUploading(true)
    try {
      const res = await api.blog.mine.uploadImage(file)
      const url = res?.data?.url
      if (!url) throw new Error('The upload finished but no image URL came back.')
      onChange(url)
    } catch (err) {
      onError(err?.message || 'Could not upload the image. Please try again.')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="flex items-center gap-4">
      <div className="w-28 h-20 shrink-0 rounded-xl border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center">
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : (
          <LuImage className="w-6 h-6 text-stone-300" />
        )}
      </div>
      <div className="min-w-0">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="rounded-xl border border-stone-200 px-3.5 py-2 text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : value ? 'Replace image' : 'Choose image'}
          </button>
          {value && !uploading && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-stone-500 hover:text-red-600"
            >
              <LuTrash2 className="w-3.5 h-3.5" /> Remove
            </button>
          )}
        </div>
        <p className="text-[10px] text-stone-400 mt-1.5">
          Landscape works best. JPG, PNG or WebP up to 10MB.
        </p>
      </div>
    </div>
  )
}

export default function BlogComposerModal({ post = null, onClose }) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(post?.id)
  const [banner, setBanner] = useState(null)
  const [mode, setMode] = useState('write')

  const {
    register,
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: post?.title ?? '',
      // `content_source` is the author's own text; `content` is the HTML the
      // server generated from it, which is not what anyone edits.
      content: post?.content_source ?? '',
      excerpt: post?.excerpt ?? '',
      blog_category_id: post?.blog_category_id ?? '',
      featured_image_url: post?.featured_image_url ?? '',
    },
    mode: 'onTouched',
  })

  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  // The admin's taxonomy, read from the public endpoint — an author picks a
  // category but cannot add one.
  const { data: categories = [], isPending: categoriesLoading } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.blog.categories(),
  })
  const categoryOptions = useMemo(
    () => (Array.isArray(categories) ? categories : []).map((c) => ({ value: c.id, label: c.name })),
    [categories],
  )

  const source = watch('content')
  const previewHtml = useMemo(() => (mode === 'preview' ? renderAuthorContent(source) : ''), [mode, source])
  const stats = readingStats(source)

  const save = useMutation({
    mutationFn: ({ payload, submit }) => {
      const body = { ...payload, submit }
      return isEdit ? api.blog.mine.update(post.id, body) : api.blog.mine.create(body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'my-blog-posts'] })
      onClose()
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; field names
      // match the payload keys 1:1, so they surface inline as-is.
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner(err?.message || 'Could not save the article. Please try again.')
    },
  })

  const submitWith = (submit) =>
    handleSubmit((v) => {
      setBanner(null)
      const payload = {
        title: v.title.trim(),
        content: v.content ?? '',
        excerpt: v.excerpt?.trim() ?? '',
        blog_category_id: v.blog_category_id ? Number(v.blog_category_id) : undefined,
        featured_image_url: v.featured_image_url || undefined,
      }
      if (submit && !payload.content.trim()) {
        setError('content', { type: 'manual', message: 'Write the article before submitting it for review.' })
        setMode('write')
        return
      }
      save.mutate({ payload, submit })
    })

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`
  const busy = save.isPending

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit article' : 'Write an article'}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Wisdom Journal</span>
            <h3 className="text-xl text-astroDark font-royal">{isEdit ? 'Edit article' : 'Write an article'}</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              Saved as your draft. An admin reads it before it goes on the public blog.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {banner && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {banner}
          </div>
        )}

        {post?.status === 'rejected' && post?.rejection_reason && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-100">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">What the admin asked for</p>
            <p className="text-xs text-amber-900 mt-1 leading-relaxed">{post.rejection_reason}</p>
          </div>
        )}

        <form className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Title" error={errors.title?.message} className="sm:col-span-2">
            <input
              className={cls('title')}
              maxLength={MAX_TITLE}
              placeholder="e.g. What a Saturn transit really asks of you"
              {...register('title', { required: 'Your article needs a title' })}
            />
          </Field>

          <Field label="Category" error={errors.blog_category_id?.message}>
            <Controller
              name="blog_category_id"
              control={control}
              render={({ field }) => (
                <Select
                  inputId="blog-composer-category"
                  options={categoryOptions}
                  value={categoryOptions.find((o) => String(o.value) === String(field.value)) ?? null}
                  onChange={(option) => field.onChange(option?.value ?? '')}
                  onBlur={field.onBlur}
                  placeholder={categoriesLoading ? 'Loading categories…' : 'Choose a category'}
                  styles={selectStyles}
                  isLoading={categoriesLoading}
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            />
          </Field>

          <Field label="Cover image (optional)" className="sm:col-span-1">
            <Controller
              name="featured_image_url"
              control={control}
              render={({ field }) => (
                <CoverImageField value={field.value} onChange={field.onChange} onError={setBanner} />
              )}
            />
          </Field>

          <Field
            label="Article"
            error={errors.content?.message}
            hint={`${stats.words} words · about ${stats.minutes} min read`}
            className="sm:col-span-2"
          >
            {/* Write / Preview, so the author can see what the formatting does
                before an admin does. */}
            <div className="flex items-center gap-1 mb-2">
              {[
                { key: 'write', label: 'Write', icon: LuPencil },
                { key: 'preview', label: 'Preview', icon: LuEye },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold transition-colors ${
                    mode === key ? 'bg-stone-100 text-astroDark' : 'text-stone-400 hover:text-stone-600'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            {mode === 'write' ? (
              <textarea
                rows={14}
                maxLength={MAX_CONTENT}
                className={`${cls('content')} font-mono text-[12.5px] leading-relaxed`}
                placeholder={'Write in plain text.\n\n## Use a heading like this\n\nA blank line starts a new paragraph. **Bold** and *italic* work, and a line starting with "- " becomes a bullet.'}
                {...register('content', { maxLength: { value: MAX_CONTENT, message: 'The article is too long.' } })}
              />
            ) : (
              <div className="rounded-xl border border-stone-200 bg-white px-4 py-3 min-h-[20rem] max-h-[28rem] overflow-y-auto">
                {previewHtml ? (
                  <div className="blog-preview text-sm text-stone-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <p className="text-xs text-stone-400">Nothing to preview yet.</p>
                )}
              </div>
            )}

            {/* The cheatsheet is the API's own list, so it can never promise
                formatting the server does not render. */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {BLOG_MARKUP_HELP.map((item) => (
                <span key={item.syntax} className="text-[10px] text-stone-400">
                  <code className="text-stone-500 font-semibold">{item.syntax}</code> — {item.means}
                </span>
              ))}
            </div>
          </Field>

          <Field
            label="Summary (optional)"
            error={errors.excerpt?.message}
            hint="Shown on the blog listing card"
            className="sm:col-span-2"
          >
            <textarea
              rows={2}
              maxLength={MAX_EXCERPT}
              className={cls('excerpt')}
              placeholder="One or two lines. Left empty, we use the opening of your article."
              {...register('excerpt', { maxLength: { value: MAX_EXCERPT, message: 'Keep the summary under 500 characters.' } })}
            />
          </Field>

          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
            <button
              type="button"
              disabled={busy}
              onClick={submitWith(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#C8922A] px-5 py-3 text-sm font-bold text-white hover:bg-[#b07f22] disabled:opacity-50"
            >
              <LuSend className="w-4 h-4" />
              {busy ? 'Working…' : 'Submit for review'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={submitWith(false)}
              className="inline-flex items-center justify-center rounded-xl border border-stone-200 px-5 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            >
              Save draft
            </button>
            <p className="text-[11px] text-stone-400 sm:ml-auto">
              You can keep editing a draft. Once submitted, it is locked until the admin decides.
            </p>
          </div>
        </form>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
